import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { Flex } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { fetchLegalOnboardingRequestsHandler } from "./schema/handler-functions";
import {
  GetLegalOnboardingRequestsFilters,
  legalOnboardingFilterSchema,
  LegalOnboardingListResponse,
} from "./schema/legal-onboarding.schema";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { NoData } from "@/components/common/section-components/NoData";
import { legalOnboardingHeaderElements } from "./schema/header-elements";
import { LegalOnboardingFilter } from "@/components/common/section-components/filters/LegalOnboardingFilter";
import {
  clearFiltersHandler,
  submitFiltersHandler,
} from "./schema/handler-filters";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { LegalOnboardingStatus } from "@/constants/entity-status";
import { AppRoutes } from "@/constants/routes";
import { useNavigate } from "react-router-dom";
import { LegalOnboardingActions } from "@/components/common/section-components/action/LegalOnboardingActions";
import { LegalOnboardingListItem } from "@/services/legal-onboarding.service";

export const LegalOnboardingPage = () => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [currentFilters, setCurrentFilters] =
    useState<GetLegalOnboardingRequestsFilters>({});
  const [list, setList] = useState<LegalOnboardingListResponse>({
    totalRecords: 0,
    totalPages: 0,
    data: [],
  });
  const [page, setPage] = useState<number>(1);

  const { handleSubmit, getValues, setValue, control, watch } = useForm({
    resolver: zodResolver(legalOnboardingFilterSchema),
  });

  const isFormEmpty = Object.values(watch()).every(
    (val) =>
      val === undefined || val === "" || (Array.isArray(val) && !val.length),
  );

  const fetchLegalOnboardingRequests = async (
    filters?: GetLegalOnboardingRequestsFilters,
  ) => {
    await fetchLegalOnboardingRequestsHandler({
      setLoading,
      setList,
      filters,
    });
  };

  useEffect(() => {
    if (isActive) {
      fetchLegalOnboardingRequests({
        ...currentFilters,
        "P.Pagina": page,
        "P.Records": 10,
      });
    }
  }, [isActive]);

  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    await submitFiltersHandler({
      event,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      fetchLegalOnboardingRequests: (filters) =>
        fetchLegalOnboardingRequests({
          ...filters,
          "P.Pagina": 1,
          "P.Records": 10,
        }),
    });
  };

  const clearFilters = async () => {
    setPage(1);
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
    });
    setCurrentFilters({});
  };

  const handlePageChange = (page: number) => {
    setPage(page);
    fetchLegalOnboardingRequests({
      ...currentFilters,
      "P.Pagina": page,
      "P.Records": 10,
    });
  };

  const handleDetail = (id: number) => {
    navigate(AppRoutes.LEGAL_ONBOARDING_DETAIL.replace(":id", String(id)), {
      state: { page },
    });
  };

  const getActionElements = (item: LegalOnboardingListItem) => (
    <LegalOnboardingActions handleDetail={handleDetail} item={item} />
  );

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      data-total-records={list?.totalRecords ?? 0}
    >
      <SectionHeader title="Onboarding Jurídico" loading={loading} />

      {/* Filtros del listado */}

      <LegalOnboardingFilter
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        control={control}
        disableFilters={loading}
        loadingFilters={loading}
        filtersApplied={filtersApplied}
        disableClear={isFormEmpty}
      />

      {/* Tabla de solicitudes de onboarding */}

      <Flex flexDir="column" paddingX="20px">
        {Boolean(!loading && !list.data.length) && <NoData />}
        {Boolean(!loading && list.data.length) && (
          <>
            <StandardTable
              noSelection
              headers={legalOnboardingHeaderElements}
              items={list?.data?.map((el) => ({
                ...el,
                estado: (
                  <RequestStatusBadge
                    status={el.estado as LegalOnboardingStatus}
                  />
                ),
              }))}
              actionElements={getActionElements}
            />

            {list.totalPages > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={list.totalRecords}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
